// There is currently an error with eslint conflicting with the rules of prettier on indentation.
// For now, disabling the indent rule for this file.  Once the conflict is resolved, please re-enable the indent rule.
/* eslint-disable indent */
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../context/analytics-context/analytics-context';
import { setIsWamDrawerOpen, useAppContext } from '../../../context/app-context';
import { setIsToasterOpen } from '../../../context/app-context/app-context-actions';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { getObjectByName, getGroupedItemByName, CanvasDataTypes, togglePhotoTextZoneButtons } from '../../../utils';
import { handleTemporaryTextEditor, HandleTemporaryTextEditorProps } from '../utils/handle-temporary-text-editor';
import { useIsSignAndSend } from '../../../hooks';

type HandleTypeAMessageProps = {
  canvas: fabric.Canvas;
  button?: fabric.Object;
};

type HandleAddHandwritingProps = {
  canvas: fabric.Canvas;
  button?: fabric.Object;
};

type UsePhotoTextZoneControlsReturn = {
  handleTypeAMessage: (props: HandleTypeAMessageProps) => void;
  handleAddHandwriting: (props: HandleAddHandwritingProps) => void;
};

// Custom hook for photo text area controls
export const usePhotoTextZoneControls = (): UsePhotoTextZoneControlsReturn => {
  const { IS_SNS_TEXTBOX_OVERLAY_ENABLED } = useFeatureFlags();
  const isSignAndSend = useIsSignAndSend();
  const { updateEditFormats, firstElementSelected, trackInitializeEdit } = useAnalyticsContext();
  const { t } = useTranslation();
  const { appDispatch } = useAppContext();

  // Handles user zone control errors by showing a toaster and throwing the error
  const onUserZoneControlError = (error: Error): void => {
    setIsToasterOpen(appDispatch, {
      title: t('generalError.title'),
      children: t('generalError.message'),
      variant: ToastVariants.Error,
    });

    throw error;
  };

  // Handles the logic for the 'Type a Message' button, including opening the temporary text editor and updating analytics
  const handleTypeAMessage = ({ canvas, button }: HandleTypeAMessageProps): void => {
    const handleTemporaryTextEditorProps: HandleTemporaryTextEditorProps = {
      currentCanvas: canvas,
      zone: null,
    };

    if (button) {
      const photoTextZoneId = button.data.photoTextZoneId;
      const photoTextZone = getObjectByName(photoTextZoneId, canvas) as fabric.Group;
      if (!photoTextZone) {
        onUserZoneControlError(new Error('Photo text zone not found'));
      }
      handleTemporaryTextEditorProps.zone = photoTextZone;
    }

    const activePhotoTextZone = canvas.getActiveObject() as fabric.Group;

    const originalTextbox =
      activePhotoTextZone?.type === 'group'
        ? (getGroupedItemByName(
            CanvasDataTypes.PhotoZoneTextbox,
            activePhotoTextZone as fabric.Group,
          ) as fabric.Textbox)
        : null;

    togglePhotoTextZoneButtons(false, canvas, activePhotoTextZone);
    if (IS_SNS_TEXTBOX_OVERLAY_ENABLED && isSignAndSend) {
      if (activePhotoTextZone && originalTextbox) {
        const { top, left, height, width } = activePhotoTextZone.getBoundingRect();
        originalTextbox?.set({
          top: Math.ceil(top),
          left: Math.ceil(left),
          width: Math.ceil(width),
          height: Math.ceil(height),
        });
      }
      // Add defensive check to ensure textbox has required data.ID before entering editing mode
      if (!originalTextbox?.data?.ID) {
        // eslint-disable-next-line no-console
        console.warn('[usePhotoTextZoneControls] Cannot enter editing mode: originalTextbox missing data.ID', {
          hasTextbox: !!originalTextbox,
          textboxName: originalTextbox?.name,
          textboxType: originalTextbox?.data?.type,
        });
        return;
      }
      originalTextbox?.enterEditing();
    } else {
      handleTemporaryTextEditor(handleTemporaryTextEditorProps);
    }

    const isTamEdited = (originalTextbox as fabric.Textbox)?.text?.length;
    updateEditFormats({ eventAction: isTamEdited ? 'format' : 'add' });
    if (!firstElementSelected.current) {
      updateEditFormats({ eventAction: 'start' });
      trackInitializeEdit('photo');
      firstElementSelected.current = true;
    }
  };

  /**
   * Handle add handwriting functionality: find linked zone to set it as active and open WAM upload drawer.
   * @param props { canvas, button } phototextzone's 'add handwriting' button
   */
  // Handles the logic for the 'Add Handwriting' button, sets the active zone and opens the handwriting drawer
  const handleAddHandwriting = ({ canvas, button }: HandleAddHandwritingProps): void => {
    if (button) {
      const photoTextZone = getObjectByName(button.data.photoTextZoneId, canvas);
      if (!canvas) {
        onUserZoneControlError(new Error('Canvas is not available'));
      }

      canvas.setActiveObject(photoTextZone as fabric.Object);
    }

    setIsWamDrawerOpen(appDispatch);
    updateEditFormats({ eventAction: !firstElementSelected.current ? 'start' : 'add' });
    if (!firstElementSelected.current) {
      firstElementSelected.current = true;
    }
  };

  return { handleTypeAMessage, handleAddHandwriting };
};
